import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Avatar,
  Badge,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Button,
  Chip,
  Autocomplete,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  LinearProgress
} from '@mui/material';
import {
  Send as SendIcon,
  Chat as ChatIcon,
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Circle as CircleIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Computer as ComputerIcon,
  Image as ImageIcon,
  Poll as PollIcon,
  Event as EventIcon,
  Cancel as CancelIcon,
  Close as CloseIcon,
  CalendarMonth as CalendarMonthIcon,
  AccessTime as AccessTimeIcon,
  LocationOn as LocationOnIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '../theme/CustomThemeProvider';
import { useWebSocket } from '../contexts/WebSocketContext';
import { createApiUrl } from '../utils/apiConfig';
import { getAuthHeaders } from '../utils/authUtils';
import { useSnackbar } from './CentralSnackbar';
import { getCurrentUser } from '../utils/sessionService';
import CustomDatePicker from './CustomDatePicker';
import CustomTimePicker from './CustomTimePicker';

const MTRChatMini = ({ orientation = 'portrait' }) => {
  const { t } = useTranslation();
  const { mode } = useThemeContext();
  const { newMessage, isConnected, clearNewMessage } = useWebSocket();
  const { showError, showSuccess } = useSnackbar();
  
  // State
  const [currentUser, setCurrentUser] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [loading, setLoading] = useState(false);
  
  // UI State
  const [panelOpen, setPanelOpen] = useState(false); // Konversationsliste offen?
  const [creationMode, setCreationMode] = useState(null); // null, 'direct', 'group'
  
  // Recipient Selection
  const [availableUsers, setAvailableUsers] = useState([]);
  const [availableWorkstations, setAvailableWorkstations] = useState([]);
  const [recipientSearchQuery, setRecipientSearchQuery] = useState('');
  const [selectedRecipients, setSelectedRecipients] = useState([]); // For group
  const [selectedWorkstation, setSelectedWorkstation] = useState(null);
  const [groupName, setGroupName] = useState('');
  
  // Attachment Menu & Image Upload
  const [attachmentMenuAnchor, setAttachmentMenuAnchor] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const imageInputRef = useRef(null);
  
  // Event Dialog States
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [eventTitle, setEventTitle] = useState('');
  const [eventDescription, setEventDescription] = useState('');
  const [eventLocation, setEventLocation] = useState('');
  const [eventStartDate, setEventStartDate] = useState('');
  const [eventEndDate, setEventEndDate] = useState('');
  const [eventStartTime, setEventStartTime] = useState('');
  const [eventEndTime, setEventEndTime] = useState('');
  
  // Survey Dialog States
  const [surveyDialogOpen, setSurveyDialogOpen] = useState(false);
  const [surveyQuestion, setSurveyQuestion] = useState('');
  const [surveyOptions, setSurveyOptions] = useState(['']);
  const [surveyMultipleChoice, setSurveyMultipleChoice] = useState(false);
  const [currentOptionIndex, setCurrentOptionIndex] = useState(0);
  
  // Survey voting state - stores user votes per message
  const [surveyVotes, setSurveyVotes] = useState({});
  
  const messagesEndRef = useRef(null);
  
  // Unread count
  const totalUnread = conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0);

  // User laden
  useEffect(() => {
    const user = getCurrentUser();
    setCurrentUser(user);
  }, []);

  // Konversationen laden
  const loadConversations = async () => {
    if (!currentUser) return;
    
    try {
      const response = await fetch(createApiUrl('chat/conversations'), {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setConversations(data);
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
    }
  };

  useEffect(() => {
    if (currentUser) {
      loadConversations();
      
      // Auto-refresh alle 30 Sekunden
      const interval = setInterval(loadConversations, 30000);
      return () => clearInterval(interval);
    }
  }, [currentUser]);

  // Nachrichten laden
  const loadMessages = async (conversationId) => {
    setLoading(true);
    try {
      const response = await fetch(
        createApiUrl(`chat/conversations/${conversationId}/messages`),
        { headers: getAuthHeaders() }
      );
      
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
        
        // Als gelesen markieren
        await fetch(
          createApiUrl(`chat/conversations/${conversationId}/read`),
          { 
            method: 'POST',
            headers: getAuthHeaders()
          }
        );
        
        // Konversationen neu laden für Unread-Count
        loadConversations();
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoading(false);
    }
  };

  // Konversation auswählen
  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation);
    setSelectedWorkstation(null);
    setCreationMode(null);
    loadMessages(conversation.id);
    setPanelOpen(false); // Liste einrollen
  };

  // Zurück zur Konversationsliste
  const handleBackToList = () => {
    setSelectedConversation(null);
    setSelectedWorkstation(null);
    setMessages([]);
    setCreationMode(null);
    setSelectedRecipients([]);
    setGroupName('');
    setPanelOpen(true); // Liste ausrollen
  };

  // Neuen Chat starten
  const handleStartNewChat = (mode) => {
    setCreationMode(mode);
    setSelectedConversation(null);
    setSelectedWorkstation(null);
    setMessages([]);
    setSelectedRecipients([]);
    setGroupName('');
    setPanelOpen(false); // Schließe Liste
    
    // Lade verfügbare User + Workstations
    loadAvailableRecipients();
  };

  // Verfügbare Empfänger laden
  const loadAvailableRecipients = async () => {
    try {
      // Users laden
      const usersResponse = await fetch(createApiUrl('chat/users/search?query='), {
        headers: getAuthHeaders()
      });
      if (usersResponse.ok) {
        const users = await usersResponse.json();
        setAvailableUsers(users);
      }
      
      // Workstations laden
      const workstationsResponse = await fetch(createApiUrl('chat/workstations/search?query='), {
        headers: getAuthHeaders()
      });
      if (workstationsResponse.ok) {
        const workstations = await workstationsResponse.json();
        setAvailableWorkstations(workstations);
      }
    } catch (error) {
      console.error('Error loading recipients:', error);
    }
  };

  // Direct Chat starten (User oder Workstation)
  const handleStartDirectChatWithRecipient = async (recipient) => {
    if (recipient.type === 'workstation') {
      // Workstation: Prüfe ob User eingeloggt sind
      if (recipient.users_count === 0) {
        showError(t('chat.workstation.noUsersLoggedIn', 'Kein Benutzer angemeldet'));
        return;
      }
      
      // Setze Workstation als Ziel
      setSelectedWorkstation(recipient);
      setCreationMode(null);
      setMessages([]);
    } else {
      // User: Starte Direct Chat
      try {
        const response = await fetch(createApiUrl('chat/conversations/direct'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders()
          },
          body: JSON.stringify({ user_id: recipient.id })
        });
        
        if (response.ok) {
          const conversation = await response.json();
          await loadConversations();
          handleSelectConversation(conversation);
        }
      } catch (error) {
        console.error('Error starting direct chat:', error);
        showError(t('chat.error.startChat', 'Fehler beim Starten des Chats'));
      }
    }
  };

  // Gruppe erstellen
  const handleCreateGroup = async () => {
    if (!groupName.trim() || selectedRecipients.length === 0) return;
    
    try {
      const response = await fetch(createApiUrl('chat/conversations/group'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          name: groupName.trim(),
          member_ids: selectedRecipients.map(r => r.id)
        })
      });
      
      if (response.ok) {
        const conversation = await response.json();
        setCreationMode(null);
        setGroupName('');
        setSelectedRecipients([]);
        await loadConversations();
        handleSelectConversation(conversation);
      }
    } catch (error) {
      console.error('Error creating group:', error);
      showError(t('chat.error.createGroup', 'Fehler beim Erstellen der Gruppe'));
    }
  };

  // Nachricht senden
  const handleSendMessage = async () => {
    if (!messageText.trim() && !selectedImage) return;
    
    const messageContent = messageText.trim();
    setMessageText('');
    
    try {
      // Workstation-Nachricht
      if (selectedWorkstation) {
        const messageData = {
          text: messageContent,
          image: selectedImage ? imagePreview : null
        };
        
        const response = await fetch(
          createApiUrl(`chat/workstations/${selectedWorkstation.id}/messages`),
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...getAuthHeaders()
            },
            body: JSON.stringify(messageData)
          }
        );
        
        if (response.ok) {
          showSuccess(t('chat.workstation.messageSent', `Nachricht an ${selectedWorkstation.short_name} gesendet`));
          setMessageText('');
          setSelectedImage(null);
          setImagePreview(null);
          // Gehe zurück zur Liste
          handleBackToList();
        } else {
          showError(t('chat.error.sendMessage', 'Fehler beim Senden'));
        }
        return;
      }
      
      // Normal conversation message
      if (selectedConversation) {
        const messageData = {
          text: messageContent,
          image: selectedImage ? imagePreview : null
        };
        
        const response = await fetch(
          createApiUrl(`chat/conversations/${selectedConversation.id}/messages`),
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...getAuthHeaders()
            },
            body: JSON.stringify(messageData)
          }
        );
        
        if (response.ok) {
          const newMsg = await response.json();
          setMessages(prev => [...prev, newMsg]);
          setSelectedImage(null);
          setImagePreview(null);
          loadConversations();
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      showError(t('chat.error.sendMessage', 'Fehler beim Senden'));
    }
  };

  // Enter-Taste zum Senden
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (creationMode === 'group' && groupName.trim() && selectedRecipients.length > 0) {
        handleCreateGroup();
      } else {
        handleSendMessage();
      }
    }
  };

  // Attachment Menu Handlers
  const handleAttachmentMenuOpen = (event) => {
    setAttachmentMenuAnchor(event.currentTarget);
  };

  const handleAttachmentMenuClose = () => {
    setAttachmentMenuAnchor(null);
  };

  const handleAttachmentAction = (action) => {
    handleAttachmentMenuClose();
    
    if (action === 'image') {
      imageInputRef.current?.click();
    } else if (action === 'survey') {
      handleSurveyDialogOpen();
    } else if (action === 'event') {
      handleEventDialogOpen();
    }
  };

  // Image Upload Handlers
  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setSelectedImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
    
    // Reset input
    event.target.value = '';
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
  };

  // Event Dialog Handlers
  const handleEventDialogOpen = () => {
    setEventDialogOpen(true);
    setEventTitle('');
    setEventDescription('');
    setEventLocation('');
    setEventStartDate('');
    setEventEndDate('');
    setEventStartTime('');
    setEventEndTime('');
  };

  const handleEventDialogClose = () => {
    setEventDialogOpen(false);
  };

  const handleEventStartDateChange = (date) => {
    setEventStartDate(date);
    // Automatically set end date to same as start date if end date is empty or before start date
    if (!eventEndDate || eventEndDate < date) {
      setEventEndDate(date);
    }
  };

  const handleEventStartTimeChange = (time) => {
    setEventStartTime(time);
    // Automatically set end time to 1 hour after start time
    if (time && (!eventEndTime || eventEndTime <= time)) {
      const [hours, minutes] = time.split(':');
      const endHour = (parseInt(hours) + 1) % 24;
      const endTimeString = `${String(endHour).padStart(2, '0')}:${minutes}`;
      setEventEndTime(endTimeString);
    }
  };

  const handleSendEvent = async () => {
    if (eventTitle.trim() === '' || eventStartDate === '' || eventStartTime === '') return;
    
    if (!selectedConversation) {
      showError(t('chat.error.noConversation', 'Keine Konversation ausgewählt'));
      return;
    }

    try {
      const eventData = {
        title: eventTitle,
        description: eventDescription,
        location: eventLocation,
        startDate: eventStartDate,
        endDate: eventEndDate || eventStartDate,
        startTime: eventStartTime,
        endTime: eventEndTime || eventStartTime
      };

      const response = await fetch(
        createApiUrl(`chat/conversations/${selectedConversation.id}/messages`),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders()
          },
          body: JSON.stringify({ event: eventData })
        }
      );

      if (response.ok) {
        const newMsg = await response.json();
        setMessages(prev => [...prev, newMsg]);
        loadConversations();
        handleEventDialogClose();
        showSuccess(t('chat.event.sent', 'Ereignis gesendet'));
      } else {
        showError(t('chat.error.sendEvent', 'Fehler beim Senden des Ereignisses'));
      }
    } catch (error) {
      console.error('Error sending event:', error);
      showError(t('chat.error.sendEvent', 'Fehler beim Senden des Ereignisses'));
    }
  };

  // Survey Dialog Handlers
  const handleSurveyDialogOpen = () => {
    setSurveyDialogOpen(true);
    setSurveyQuestion('');
    setSurveyOptions(['']);
    setSurveyMultipleChoice(false);
    setCurrentOptionIndex(0);
  };

  const handleSurveyDialogClose = () => {
    setSurveyDialogOpen(false);
  };

  const handleSurveyOptionChange = (index, value) => {
    const newOptions = [...surveyOptions];
    newOptions[index] = value;
    setSurveyOptions(newOptions);
  };

  const handleSurveyOptionKeyPress = (event, index) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (surveyOptions[index].trim() !== '') {
        const newOptions = [...surveyOptions];
        newOptions.splice(index + 1, 0, '');
        setSurveyOptions(newOptions);
        setCurrentOptionIndex(index + 1);
        setTimeout(() => {
          const nextField = document.querySelector(`input[data-option-index="${index + 1}"]`);
          if (nextField) nextField.focus();
        }, 0);
      }
    }
  };

  const handleRemoveSurveyOption = (index) => {
    if (surveyOptions.length > 1) {
      const newOptions = surveyOptions.filter((_, i) => i !== index);
      setSurveyOptions(newOptions);
    }
  };

  const handleSendSurvey = async () => {
    if (!selectedConversation || surveyQuestion.trim() === '') return;
    
    const validOptions = surveyOptions.filter(opt => opt.trim() !== '');
    if (validOptions.length < 2) return;

    try {
      const surveyData = {
        question: surveyQuestion,
        options: validOptions,
        multipleChoice: surveyMultipleChoice,
        votes: validOptions.map(() => [])
      };

      const response = await fetch(createApiUrl(`chat/conversations/${selectedConversation.id}/messages`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ survey: surveyData })
      });

      if (response.ok) {
        const newMsg = await response.json();
        setMessages(prev => [...prev, newMsg]);
        loadConversations();
        handleSurveyDialogClose();
        showSuccess(t('chat.survey.sent', 'Umfrage gesendet'));
      } else {
        showError(t('chat.error.sendSurvey', 'Fehler beim Senden der Umfrage'));
      }
    } catch (error) {
      console.error('Error sending survey:', error);
      showError(t('chat.error.sendSurvey', 'Fehler beim Senden der Umfrage'));
    }
  };

  // Survey Voting Handler
  const handleSurveyVote = async (messageId, optionIndex) => {
    const voteKey = `${messageId}`;
    const userVotes = surveyVotes[voteKey] || [];
    const isVoted = userVotes.includes(optionIndex);
    
    let newUserVotes;
    if (isVoted) {
      newUserVotes = userVotes.filter(v => v !== optionIndex);
    } else {
      const msg = messages.find(m => m.id === messageId);
      if (msg && msg.survey && msg.survey.multipleChoice) {
        newUserVotes = [...userVotes, optionIndex];
      } else {
        newUserVotes = [optionIndex];
      }
    }
    
    try {
      const response = await fetch(createApiUrl(`chat/messages/${messageId}/vote`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ option_indices: newUserVotes })
      });

      if (response.ok) {
        setSurveyVotes({ ...surveyVotes, [voteKey]: newUserVotes });
        const updatedMessage = await response.json();
        setMessages(prev => prev.map(m => m.id === messageId ? updatedMessage : m));
        loadConversations();
      }
    } catch (error) {
      console.error('Error voting on survey:', error);
      showError(t('chat.error.vote', 'Fehler beim Abstimmen'));
    }
  };

  // Generate ICS file for calendar import
  const generateICS = (event) => {
    const formatICSDate = (date, time) => {
      const dateTime = new Date(`${date}T${time}`);
      return dateTime.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };

    const now = new Date().toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    const startDateTime = formatICSDate(event.startDate, event.startTime);
    const endDateTime = formatICSDate(event.endDate || event.startDate, event.endTime);
    
    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Reporting System//Chat Event//DE',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      `UID:${now}@reporting-chat`,
      `DTSTAMP:${now}`,
      `DTSTART:${startDateTime}`,
      `DTEND:${endDateTime}`,
      `SUMMARY:${event.title}`,
      event.description ? `DESCRIPTION:${event.description.replace(/\n/g, '\\n')}` : '',
      event.location ? `LOCATION:${event.location}` : '',
      'STATUS:CONFIRMED',
      'SEQUENCE:0',
      'END:VEVENT',
      'END:VCALENDAR'
    ].filter(line => line !== '').join('\r\n');

    return icsContent;
  };

  const downloadICS = (event) => {
    const icsContent = generateICS(event);
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${event.title.replace(/[^a-z0-9]/gi, '_')}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  // Auto-scroll zu neuer Nachricht
  useEffect(() => {
    if (messages.length > 0) {
      // Kleiner Delay um sicherzustellen, dass DOM gerendert ist
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [messages]);

  // Scroll beim Öffnen einer Conversation nach unten
  useEffect(() => {
    if (selectedConversation && messages.length > 0) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'instant' });
      }, 150);
    }
  }, [selectedConversation]);

  // WebSocket: Neue Nachricht empfangen
  useEffect(() => {
    if (newMessage && newMessage.type === 'new_message') {
      // Wenn Nachricht zur aktuellen Konversation gehört
      if (selectedConversation && newMessage.conversation_id === selectedConversation.id) {
        setMessages(prev => {
          // Prüfe ob Nachricht bereits existiert (z.B. Survey-Update)
          const existingIndex = prev.findIndex(m => m.id === newMessage.id);
          
          if (existingIndex !== -1) {
            // Nachricht existiert bereits → Update (z.B. Survey-Vote)
            const updated = [...prev];
            updated[existingIndex] = {
              ...updated[existingIndex],
              ...newMessage,
              text: newMessage.text || newMessage.content,
              sender_full_name: newMessage.sender_full_name || newMessage.sender_name,
              survey: newMessage.survey // Survey-Daten aktualisieren
            };
            return updated;
          } else {
            // Neue Nachricht → Hinzufügen
            const msg = {
              ...newMessage,
              text: newMessage.text || newMessage.content,
              sender_full_name: newMessage.sender_full_name || newMessage.sender_name
            };
            return [...prev, msg];
          }
        });
        
        // Als gelesen markieren
        fetch(
          createApiUrl(`chat/conversations/${selectedConversation.id}/read`),
          { method: 'POST', headers: getAuthHeaders() }
        );
      }
      
      // Konversationen-Liste aktualisieren (immer, auch wenn nicht in aktiver Conversation)
      loadConversations();
      
      // ✅ Nachricht als verarbeitet markieren
      clearNewMessage();
    }
  }, [newMessage, selectedConversation, clearNewMessage]);

  // Avatar-Initialen (aus Chat.js übernommen)
  const getInitials = (name) => {
    if (!name || typeof name !== 'string') return '?';
    
    const parts = name.trim().split(' ').filter(p => p.length > 0);
    if (parts.length === 0) return '?';
    
    // Filter out common academic titles/degrees
    const academicTitles = ['dr.', 'dr', 'prof.', 'prof', 'med.', 'med', 'phd', 'ph.d.', 'dipl.', 'dipl'];
    const filteredParts = parts.filter(part => 
      !academicTitles.includes(part.toLowerCase().replace(/\./g, ''))
    );
    
    const nameParts = filteredParts.length > 0 ? filteredParts : parts;
    
    if (nameParts.length >= 2) {
      return `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase();
    }
    
    return nameParts[0].substring(0, 2).toUpperCase();
  };

  // Avatar CSS class (aus Chat.js übernommen)
  const getAvatarClass = (type) => {
    if (type === 'group') return 'chat-group-avatar';
    return 'chat-user-avatar';
  };

  // Konversations-Name formatieren
  const getConversationName = (conversation) => {
    if (conversation.is_group) {
      return conversation.name || t('chat.groupChat', 'Gruppenchat');
    }
    
    // Direct Chat: Backend liefert bereits den korrekten Namen
    // Fallback: Suche in members (nicht participants!)
    if (conversation.name) {
      return conversation.name;
    }
    
    const members = conversation.members || conversation.participants || [];
    const otherUser = members.find(p => p.user_id !== currentUser?.id);
    if (otherUser) {
      const parts = [
        otherUser.academic_degree,
        otherUser.first_name,
        otherUser.last_name
      ].filter(Boolean);
      return parts.join(' ') || otherUser.username || '';
    }
    
    return '';
  };

  // Online-Status des Chat-Partners prüfen
  const isPartnerOnline = (conversation) => {
    if (conversation.is_group) return false;
    const members = conversation.members || conversation.participants || [];
    const otherUser = members.find(p => p.user_id !== currentUser?.id);
    return otherUser?.is_online || false;
  };

  // Nachricht-Zeit formatieren
  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';
    
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return '';
      
      const now = new Date();
      const isToday = date.toDateString() === now.toDateString();
      
      if (isToday) {
        return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
      }
      return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
    } catch (e) {
      console.error('Error formatting date:', e, timestamp);
      return '';
    }
  };

  // Format recipient for display
  const formatRecipient = (recipient) => {
    if (recipient.type === 'workstation') {
      return `${recipient.short_name} - ${recipient.name}`;
    }
    const parts = [recipient.title, recipient.first_name, recipient.last_name].filter(Boolean);
    return parts.join(' ') || recipient.username || '';
  };

  // RENDER: Chat Toggle Button (Collapsed State)
  const renderChatButton = () => (
    <Button
      onClick={() => setPanelOpen(!panelOpen)}
      sx={{
        width: '100%',
        height: '100%',
        minWidth: 0,
        minHeight: 0,
        p: orientation === 'landscape' ? 1 : 0.5,
        backgroundColor: 'var(--rd-bg-subtle)',
        color: 'var(--rd-primary)',
        border: `1px solid ${'var(--rd-divider)'}`,
        borderRadius: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.5,
        writingMode: orientation === 'landscape' ? 'vertical-rl' : 'horizontal-tb',
        transform: orientation === 'landscape' ? 'rotate(180deg)' : 'none',
        '&:hover': {
          backgroundColor: 'var(--rd-bg-elevated)'
        }
      }}
    >
      <ChatIcon sx={{ fontSize: orientation === 'landscape' ? '1rem' : '0.9rem' }} />
      <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: orientation === 'landscape' ? '0.8rem' : '0.75rem' }}>
        {t('chat.title', 'Chats')} {totalUnread > 0 && `(${totalUnread})`}
      </Typography>
    </Button>
  );

  // RENDER: Konversationsliste (Expanded State)
  const renderConversationList = () => (
    <Box sx={{
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'var(--rd-bg-default)',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{
        p: 0.5,
        backgroundColor: 'var(--rd-bg-subtle)',
        borderBottom: `1px solid ${'var(--rd-divider)'}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <ChatIcon sx={{ fontSize: '0.9rem', color: 'var(--rd-primary)' }} />
          <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'var(--rd-primary)', fontSize: '0.75rem' }}>
            {t('chat.title', 'Chats')} ({totalUnread})
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={() => setPanelOpen(false)}
          sx={{ p: 0.25 }}
        >
          <ArrowBackIcon sx={{ 
            fontSize: '0.9rem',
            transform: orientation === 'portrait' ? 'rotate(270deg)' : 'rotate(180deg)'
          }} />
        </IconButton>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ 
        p: 0.5, 
        display: 'flex', 
        gap: 0.5,
        borderBottom: `1px solid ${'var(--rd-divider)'}`,
        flexShrink: 0
      }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<PersonIcon sx={{ fontSize: '0.8rem' }} />}
          onClick={() => handleStartNewChat('direct')}
          sx={{
            flex: 1,
            fontSize: '0.65rem',
            py: 0.25,
            borderColor: 'var(--rd-primary)',
            color: 'var(--rd-primary)',
            '&:hover': { borderColor: 'var(--rd-primary-hover)', backgroundColor: 'color-mix(in srgb, var(--rd-primary) 10%, transparent)' }
          }}
        >
          {t('chat.newChat', 'Neuer Chat')}
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<GroupIcon sx={{ fontSize: '0.8rem' }} />}
          onClick={() => handleStartNewChat('group')}
          sx={{
            flex: 1,
            fontSize: '0.65rem',
            py: 0.25,
            borderColor: 'var(--rd-primary)',
            color: 'var(--rd-primary)',
            '&:hover': { borderColor: 'var(--rd-primary-hover)', backgroundColor: 'color-mix(in srgb, var(--rd-primary) 10%, transparent)' }
          }}
        >
          {t('chat.newGroup', 'Neue Gruppe')}
        </Button>
      </Box>

      {/* Konversationsliste */}
      <Box sx={{
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        scrollbarWidth: 'thin',
        '&::-webkit-scrollbar': { width: '4px' },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'var(--rd-border)',
          borderRadius: '2px'
        }
      }}>
        {conversations.length === 0 ? (
          <Box sx={{ p: 1, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              {t('chat.noConversations', 'Keine Chats')}
            </Typography>
          </Box>
        ) : (
          <List dense sx={{ p: 0 }}>
            {conversations.map((conv) => (
              <ListItem
                key={conv.id}
                onClick={() => handleSelectConversation(conv)}
                sx={{
                  py: 0.5,
                  px: 0.5,
                  cursor: 'pointer',
                  borderBottom: `1px solid ${'var(--rd-divider)'}`,
                  '&:hover': {
                    backgroundColor: mode === 'dark' ? 'color-mix(in srgb, var(--rd-primary) 10%, transparent)' : 'color-mix(in srgb, var(--rd-primary) 8%, transparent)'
                  }
                }}
              >
                <ListItemAvatar sx={{ minWidth: '32px' }}>
                  <Badge
                    badgeContent={conv.unread_count || 0}
                    color="error"
                    overlap="circular"
                    sx={{
                      '& .MuiBadge-badge': {
                        fontSize: '0.6rem',
                        height: '8px',
                        minWidth: '8px',
                        padding: '0 3px'
                      }
                    }}
                  >
                    <Badge
                      variant="dot"
                      color="success"
                      invisible={!isPartnerOnline(conv)}
                      overlap="circular"
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                      }}
                    >
                      <Avatar
                        className={getAvatarClass(conv.is_group ? 'group' : 'user')}
                        sx={{
                          width: 28,
                          height: 28,
                          fontSize: '0.7rem'
                        }}
                      >
                        {conv.is_group ? <GroupIcon sx={{ fontSize: '0.9rem' }} /> : getInitials(getConversationName(conv))}
                      </Avatar>
                    </Badge>
                  </Badge>
                </ListItemAvatar>
                <ListItemText
                  primary={getConversationName(conv)}
                  secondary={(() => {
                    const lastMsg = conv.last_message || '';
                    if (!lastMsg) return t('chat.noMessages', 'Keine Nachrichten');
                    return lastMsg.length > 30 ? lastMsg.substring(0, 30) + '...' : lastMsg;
                  })()}
                  primaryTypographyProps={{
                    variant: 'caption',
                    sx: {
                      fontWeight: conv.unread_count > 0 ? 'bold' : 'normal',
                      fontSize: '0.75rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }
                  }}
                  secondaryTypographyProps={{
                    variant: 'caption',
                    sx: {
                      fontSize: '0.65rem',
                      fontWeight: conv.unread_count > 0 ? 'bold' : 'normal',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }
                  }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    </Box>
  );

  // RENDER: Empfängerauswahl (Direct Chat oder Gruppe)
  const renderRecipientSelection = () => (
    <Box sx={{
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'var(--rd-bg-default)',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{
        p: 0.5,
        backgroundColor: 'var(--rd-bg-subtle)',
        borderBottom: `1px solid ${'var(--rd-divider)'}`,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        flexShrink: 0
      }}>
        <IconButton
          size="small"
          onClick={handleBackToList}
          sx={{ p: 0.25 }}
        >
          <ArrowBackIcon sx={{ fontSize: '0.9rem' }} />
        </IconButton>
        <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
          {creationMode === 'group' ? t('chat.createGroup', 'Gruppe erstellen') : t('chat.selectRecipient', 'Empfänger auswählen')}
        </Typography>
      </Box>

      <Box sx={{ p: 0.5, flex: 1, overflow: 'auto' }}>
        {creationMode === 'group' && (
          <TextField
            fullWidth
            size="small"
            label={t('chat.groupName', 'Gruppenname')}
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            sx={{ mb: 1, '& input': { fontSize: '0.75rem' } }}
          />
        )}

        <Autocomplete
          multiple={creationMode === 'group'}
          options={[...availableUsers.map(u => ({ ...u, type: 'user' })), ...availableWorkstations]}
          groupBy={(option) => option.type === 'workstation' ? t('chat.workstations', 'Arbeitsplätze') : t('chat.users', 'Benutzer')}
          getOptionLabel={(option) => formatRecipient(option)}
          isOptionEqualToValue={(option, value) => option.type === value.type && option.id === value.id}
          value={creationMode === 'group' ? selectedRecipients : null}
          onChange={(e, newValue) => {
            if (creationMode === 'group') {
              setSelectedRecipients(newValue);
            } else if (newValue) {
              handleStartDirectChatWithRecipient(newValue);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              placeholder={t('chat.searchUsersOrWorkstations', 'Benutzer oder Arbeitsplatz suchen...')}
              sx={{ '& input': { fontSize: '0.75rem' } }}
            />
          )}
          renderOption={(props, option) => {
            const { key, ...otherProps } = props;
            return (
              <Box component="li" key={key} {...otherProps} sx={{ fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                {option.type === 'workstation' ? (
                  <>
                    <ComputerIcon sx={{ fontSize: '0.9rem', color: 'var(--rd-primary)' }} />
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                        {option.short_name}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', display: 'block' }}>
                        {option.name} ({option.users_count} {t('chat.workstation.loggedIn', 'angemeldet')})
                      </Typography>
                    </Box>
                  </>
                ) : (
                  <>
                    <PersonIcon sx={{ fontSize: '0.9rem' }} />
                    <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                      {formatRecipient(option)}
                    </Typography>
                  </>
                )}
              </Box>
            );
          }}
          sx={{ fontSize: '0.75rem' }}
        />

        {creationMode === 'group' && (
          <Box sx={{ mt: 1 }}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleCreateGroup}
              disabled={!groupName.trim() || selectedRecipients.length === 0}
              sx={{
                fontSize: '0.75rem',
                backgroundColor: 'var(--rd-primary)',
                '&:hover': { backgroundColor: 'var(--rd-primary-hover)' }
              }}
            >
              {t('common.create', 'Erstellen')}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );

  // RENDER: Aktiver Chat
  const renderActiveChat = () => {
    const chatTitle = selectedWorkstation 
      ? `${selectedWorkstation.short_name} - ${selectedWorkstation.name}`
      : getConversationName(selectedConversation);
    
    const showOnlineStatus = selectedConversation && !selectedConversation.is_group;

    return (
      <Box sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'var(--rd-bg-default)',
        overflow: 'hidden'
      }}>
        {/* Header mit Chatpartner und Online-Status */}
        <Box sx={{
          p: 0.5,
          backgroundColor: 'var(--rd-bg-subtle)',
          borderBottom: `1px solid ${'var(--rd-divider)'}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0
        }}>
          <IconButton
            size="small"
            onClick={handleBackToList}
            sx={{ p: 0.25, mr: 0.5 }}
          >
            <ArrowBackIcon sx={{ fontSize: '0.9rem' }} />
          </IconButton>
          
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {selectedWorkstation && (
              <ComputerIcon sx={{ fontSize: '0.9rem', color: 'var(--rd-primary)' }} />
            )}
            <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
              {chatTitle}
            </Typography>
            {showOnlineStatus && (
              <Chip
                icon={<CircleIcon sx={{ fontSize: '0.5rem' }} />}
                label={isPartnerOnline(selectedConversation) ? t('chat.online', 'Online') : t('chat.offline', 'Offline')}
                size="small"
                sx={{
                  height: '16px',
                  fontSize: '0.6rem',
                  '& .MuiChip-icon': {
                    color: isPartnerOnline(selectedConversation) ? 'var(--rd-success)' : 'var(--rd-text-disabled)'
                  }
                }}
              />
            )}
          </Box>
        </Box>

        {/* Nachrichten */}
        <Box sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          p: 0.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'var(--rd-border)',
            borderRadius: '2px'
          }
        }}>
          {loading ? (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
              {t('common.loading', 'Laden...')}
            </Typography>
          ) : messages.length === 0 ? (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
              {t('chat.noMessages', 'Keine Nachrichten')}
            </Typography>
          ) : (
            messages.map((msg) => {
              const isOwn = msg.sender_id === currentUser?.id;
              return (
                <Box
                  key={msg.id}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: isOwn ? 'flex-end' : 'flex-start',
                    mb: 0.5
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: '85%',
                      backgroundColor: isOwn 
                        ? 'var(--rd-primary)'
                        : 'var(--rd-bg-elevated)',
                      color: isOwn ? 'var(--rd-primary-contrast)' : 'var(--rd-text-primary)',
                      borderRadius: '8px',
                      p: 0.5,
                      wordBreak: 'break-word'
                    }}
                  >
                    {!isOwn && msg.sender_full_name && (
                      <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.65rem', display: 'block', mb: 0.25 }}>
                        {msg.sender_full_name}
                      </Typography>
                    )}
                    {msg.image && (
                      <Box
                        component="img"
                        src={msg.image}
                        alt="Attachment"
                        sx={{
                          maxWidth: 150,
                          maxHeight: 150,
                          borderRadius: 1,
                          display: 'block',
                          mb: 0.5,
                          cursor: 'pointer'
                        }}
                      />
                    )}
                    {msg.event && (
                      <Box sx={{ minWidth: 180, maxWidth: 250, mb: 0.5 }}>
                        {/* Event Icon and Title */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                          <CalendarMonthIcon sx={{ fontSize: '0.9rem', color: isOwn ? 'var(--rd-primary-contrast)' : 'primary.main' }} />
                          <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                            {msg.event.title}
                          </Typography>
                        </Box>

                        {/* Date and Time */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                          <AccessTimeIcon sx={{ fontSize: '0.7rem', opacity: 0.7 }} />
                          <Typography variant="caption" sx={{ fontSize: '0.65rem', opacity: 0.9 }}>
                            {new Date(msg.event.startDate).toLocaleDateString('de-DE', { 
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric'
                            })}
                            {' • '}
                            {msg.event.startTime}
                            {msg.event.endTime && msg.event.endTime !== msg.event.startTime && ` - ${msg.event.endTime}`}
                          </Typography>
                        </Box>

                        {/* Location */}
                        {msg.event.location && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <LocationOnIcon sx={{ fontSize: '0.7rem', opacity: 0.7 }} />
                            <Typography variant="caption" sx={{ fontSize: '0.65rem', opacity: 0.9 }}>
                              {msg.event.location}
                            </Typography>
                          </Box>
                        )}

                        {/* Description */}
                        {msg.event.description && (
                          <Typography variant="caption" sx={{ fontSize: '0.65rem', display: 'block', mt: 0.5, mb: 0.5, opacity: 0.9 }}>
                            {msg.event.description}
                          </Typography>
                        )}

                        {/* Download Button */}
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<DownloadIcon sx={{ fontSize: '0.7rem' }} />}
                          onClick={() => downloadICS(msg.event)}
                          fullWidth
                          sx={{
                            mt: 0.5,
                            fontSize: '0.6rem',
                            py: 0.25,
                            borderColor: isOwn ? 'rgba(255,255,255,0.5)' : 'primary.main',
                            color: isOwn ? 'var(--rd-primary-contrast)' : 'primary.main',
                            '&:hover': {
                              borderColor: isOwn ? 'rgba(255,255,255,0.8)' : 'primary.dark',
                              backgroundColor: isOwn ? 'rgba(255,255,255,0.1)' : 'color-mix(in srgb, var(--rd-primary) 10%, transparent)'
                            }
                          }}
                        >
                          {t('chat.event.downloadCalendar', 'Zu Kalender hinzufügen')}
                        </Button>
                      </Box>
                    )}
                    {msg.survey && (
                      <Box sx={{ minWidth: 200, maxWidth: 300, mb: 0.5 }}>
                        {/* Question */}
                        <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.75rem', display: 'block', mb: 0.5 }}>
                          <PollIcon sx={{ fontSize: '0.8rem', mr: 0.5, verticalAlign: 'middle' }} />
                          {msg.survey.question}
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.6rem', opacity: 0.8, display: 'block', mb: 0.5 }}>
                          {msg.survey.multipleChoice 
                            ? t('chat.survey.selectMinimumMultiple', 'Wähle mindestens eine Option aus.')
                            : t('chat.survey.selectMinimumSingle', 'Wähle eine Option aus.')
                          }
                        </Typography>
                        
                        {/* Options */}
                        {(() => {
                          const totalVotes = msg.survey.votes.reduce((sum, voters) => sum + voters.length, 0);
                          const voteKey = `${msg.id}`;
                          const userVotes = surveyVotes[voteKey] || [];
                          
                          return (
                            <>
                              {msg.survey.options.map((option, optIndex) => {
                                const isVoted = userVotes.includes(optIndex);
                                const voteCount = msg.survey.votes[optIndex]?.length || 0;
                                const percentage = totalVotes > 0 ? Math.round((voteCount / totalVotes) * 100) : 0;
                                
                                return (
                                  <Box
                                    key={optIndex}
                                    onClick={() => handleSurveyVote(msg.id, optIndex)}
                                    sx={{
                                      position: 'relative',
                                      mb: 0.5,
                                      p: 0.5,
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      border: `1px solid ${isVoted ? (isOwn ? 'rgba(255,255,255,0.8)' : 'primary.main') : 'rgba(128,128,128,0.3)'}`,
                                      backgroundColor: isVoted 
                                        ? (isOwn ? 'rgba(255,255,255,0.15)' : 'color-mix(in srgb, var(--rd-primary) 10%, transparent)') 
                                        : 'transparent',
                                      '&:hover': {
                                        backgroundColor: isOwn ? 'rgba(255,255,255,0.1)' : 'color-mix(in srgb, var(--rd-primary) 5%, transparent)'
                                      }
                                    }}
                                  >
                                    {/* Progress bar */}
                                    <Box sx={{
                                      position: 'absolute',
                                      left: 0,
                                      top: 0,
                                      bottom: 0,
                                      width: `${percentage}%`,
                                      backgroundColor: isOwn ? 'rgba(255,255,255,0.2)' : 'color-mix(in srgb, var(--rd-primary) 20%, transparent)',
                                      borderRadius: '4px',
                                      transition: 'width 0.3s ease',
                                      zIndex: 0
                                    }} />
                                    
                                    {/* Option text and count */}
                                    <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: isVoted ? 'bold' : 'normal' }}>
                                        {option}
                                      </Typography>
                                      <Typography variant="caption" sx={{ fontSize: '0.6rem', ml: 1 }}>
                                        {voteCount} ({percentage}%)
                                      </Typography>
                                    </Box>
                                  </Box>
                                );
                              })}
                              
                              {/* Total votes */}
                              <Typography variant="caption" sx={{ fontSize: '0.6rem', opacity: 0.7, display: 'block', mt: 0.5 }}>
                                {totalVotes} {totalVotes === 1 ? t('chat.survey.vote', 'Stimme') : t('chat.survey.votes', 'Stimmen')}
                              </Typography>
                            </>
                          );
                        })()}
                      </Box>
                    )}
                    {(msg.text || msg.content) && (
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block' }}>
                        {msg.text || msg.content}
                      </Typography>
                    )}
                    <Typography variant="caption" sx={{ fontSize: '0.6rem', opacity: 0.7, display: 'block', mt: 0.25 }}>
                      {formatMessageTime(msg.created_at)}
                    </Typography>
                  </Box>
                </Box>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </Box>

        {/* Eingabefeld */}
        <Box sx={{
          p: 0.5,
          borderTop: `1px solid ${'var(--rd-divider)'}`,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          flexShrink: 0
        }}>
          {/* Bildvorschau */}
          {imagePreview && (
            <Box sx={{ position: 'relative', display: 'inline-block', alignSelf: 'flex-start' }}>
              <Box
                component="img"
                src={imagePreview}
                alt="Preview"
                sx={{
                  maxWidth: 100,
                  maxHeight: 100,
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'divider'
                }}
              />
              <IconButton
                size="small"
                onClick={handleRemoveImage}
                sx={{
                  position: 'absolute',
                  top: -6,
                  right: -6,
                  bgcolor: 'error.main',
                  color: 'white',
                  width: '16px',
                  height: '16px',
                  '&:hover': {
                    bgcolor: 'error.dark'
                  }
                }}
              >
                <CancelIcon sx={{ fontSize: '0.75rem' }} />
              </IconButton>
            </Box>
          )}
          
          {/* Input-Zeile */}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title={t('chat.addAttachment', 'Anhang hinzufügen')}>
              <IconButton
                size="small"
                onClick={handleAttachmentMenuOpen}
                sx={{
                  width: '28px',
                  height: '28px'
                }}
              >
                <AddIcon sx={{ fontSize: '0.9rem' }} />
              </IconButton>
            </Tooltip>
            
            <TextField
              fullWidth
              size="small"
              placeholder={t('chat.inputPlaceholder', 'Nachricht...')}
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              onKeyPress={handleKeyPress}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '0.75rem',
                  '& input': {
                    py: 0.5
                  }
                }
              }}
            />
            
            <IconButton
              size="small"
              onClick={handleSendMessage}
              disabled={!messageText.trim() && !selectedImage}
              sx={{
                backgroundColor: (messageText.trim() || selectedImage) ? 'var(--rd-primary)' : 'transparent',
                color: (messageText.trim() || selectedImage) ? 'var(--rd-primary-contrast)' : 'text.secondary',
                '&:hover': {
                  backgroundColor: (messageText.trim() || selectedImage) ? 'var(--rd-primary-hover)' : 'transparent'
                },
                width: '28px',
                height: '28px'
              }}
            >
              <SendIcon sx={{ fontSize: '0.9rem' }} />
            </IconButton>
          </Box>
        </Box>
      </Box>
    );
  };

  // MAIN RENDER
  return (
    <Box sx={{
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: orientation === 'landscape' ? 'row' : 'column',
      overflow: 'hidden',
      position: 'relative'
    }}>
      {/* Toggle Button (wenn nicht offen und kein Chat aktiv) */}
      {!panelOpen && !selectedConversation && !selectedWorkstation && creationMode === null && (
        <Box sx={{
          width: orientation === 'landscape' ? '32px' : '100%',
          height: orientation === 'landscape' ? '100%' : '32px',
          flexShrink: 0
        }}>
          {renderChatButton()}
        </Box>
      )}

      {/* Konversationsliste (Collapsible) */}
      {panelOpen && !selectedConversation && !selectedWorkstation && creationMode === null && (
        <Box sx={{
          width: orientation === 'landscape' ? '400px' : '100%',
          height: orientation === 'landscape' ? '100%' : '35vh',
          flexShrink: 0,
          borderLeft: orientation === 'landscape' ? `1px solid ${'var(--rd-divider)'}` : 'none',
          transition: 'all 0.3s ease'
        }}>
          {renderConversationList()}
        </Box>
      )}

      {/* Empfängerauswahl (Direct Chat oder Gruppe) */}
      {creationMode && (
        <Box sx={{
          width: orientation === 'landscape' ? '400px' : '100%',
          height: orientation === 'landscape' ? '100%' : '35vh',
          flexShrink: 0,
          borderLeft: orientation === 'landscape' ? `1px solid ${'var(--rd-divider)'}` : 'none'
        }}>
          {renderRecipientSelection()}
        </Box>
      )}

      {/* Aktiver Chat */}
      {(selectedConversation || selectedWorkstation) && (
        <Box sx={{
          width: orientation === 'landscape' ? '400px' : '100%',
          height: orientation === 'landscape' ? '100%' : '35vh',
          flexShrink: 0,
          borderLeft: orientation === 'landscape' ? `1px solid ${'var(--rd-divider)'}` : 'none'
        }}>
          {renderActiveChat()}
        </Box>
      )}

      {/* Attachment Menu */}
      <Menu
        anchorEl={attachmentMenuAnchor}
        open={Boolean(attachmentMenuAnchor)}
        onClose={handleAttachmentMenuClose}
      >
        <MenuItem onClick={() => handleAttachmentAction('image')}>
          <ListItemIcon>
            <ImageIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText>{t('chat.attachmentMenu.image', 'Bild')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleAttachmentAction('survey')}>
          <ListItemIcon>
            <PollIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText>{t('chat.attachmentMenu.survey', 'Umfrage')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleAttachmentAction('event')}>
          <ListItemIcon>
            <EventIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText>{t('chat.attachmentMenu.event', 'Ereignis')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Hidden file input */}
      <input
        type="file"
        ref={imageInputRef}
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleImageSelect}
      />

      {/* Event Dialog */}
      <Dialog
        open={eventDialogOpen}
        onClose={handleEventDialogClose}
        maxWidth={orientation === 'landscape' ? 'md' : 'sm'}
        fullWidth
        PaperProps={{
          sx: {
            height: orientation === 'landscape' ? '450px' : 'auto'
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{t('chat.event.createTitle', 'Ereignis erstellen')}</Typography>
            <IconButton onClick={handleEventDialogClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {orientation === 'portrait' ? (
            // Portrait Modus: Alles untereinander
            <>
              {/* Title Field */}
              <Box sx={{ mb: 2, mt: 1 }}>
                <TextField
                  autoFocus
                  fullWidth
                  label={t('chat.event.title', 'Titel')}
                  placeholder={t('chat.event.titlePlaceholder', 'Ereignis-Titel')}
                  value={eventTitle}
                  onChange={(e) => setEventTitle(e.target.value)}
                  variant="outlined"
                  required
                  size="small"
                />
              </Box>

              {/* Start Date Field */}
              <Box sx={{ mb: 2 }}>
                <CustomDatePicker
                  label={t('chat.event.startDate', 'Startdatum')}
                  value={eventStartDate}
                  onChange={(e) => handleEventStartDateChange(e.target.value)}
                  required
                  sx={{ width: '100%' }}
                />
              </Box>

              {/* Start Time Field */}
              <Box sx={{ mb: 2 }}>
                <CustomTimePicker
                  label={t('chat.event.startTime', 'Startzeit')}
                  value={eventStartTime}
                  onChange={(e) => handleEventStartTimeChange(e.target.value)}
                  required
                  sx={{ width: '100%' }}
                />
              </Box>

              {/* End Date Field */}
              <Box sx={{ mb: 2 }}>
                <CustomDatePicker
                  label={t('chat.event.endDate', 'Enddatum')}
                  value={eventEndDate}
                  onChange={(e) => setEventEndDate(e.target.value)}
                  required
                  inputProps={{ min: eventStartDate }}
                  sx={{ width: '100%' }}
                />
              </Box>

              {/* End Time Field */}
              <Box sx={{ mb: 2 }}>
                <CustomTimePicker
                  label={t('chat.event.endTime', 'Endzeit')}
                  value={eventEndTime}
                  onChange={(e) => setEventEndTime(e.target.value)}
                  sx={{ width: '100%' }}
                />
              </Box>

              {/* Location Field */}
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label={t('chat.event.location', 'Ort')}
                  placeholder={t('chat.event.locationPlaceholder', 'Veranstaltungsort')}
                  value={eventLocation}
                  onChange={(e) => setEventLocation(e.target.value)}
                  variant="outlined"
                  size="small"
                />
              </Box>

              {/* Description Field */}
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label={t('chat.event.description', 'Beschreibung')}
                  placeholder={t('chat.event.descriptionPlaceholder', 'Ereignis-Beschreibung')}
                  value={eventDescription}
                  onChange={(e) => setEventDescription(e.target.value)}
                  variant="outlined"
                  size="small"
                />
              </Box>
            </>
          ) : (
            // Landscape Modus: 4-Spalten-Layout
            <Box sx={{ display: 'flex', gap: 2, height: '100%', mt: 1 }}>
              {/* Spalte 1: Titel und Location */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  autoFocus
                  fullWidth
                  label={t('chat.event.title', 'Titel')}
                  placeholder={t('chat.event.titlePlaceholder', 'Ereignis-Titel')}
                  value={eventTitle}
                  onChange={(e) => setEventTitle(e.target.value)}
                  variant="outlined"
                  required
                  size="small"
                />
                <TextField
                  fullWidth
                  label={t('chat.event.location', 'Ort')}
                  placeholder={t('chat.event.locationPlaceholder', 'Veranstaltungsort')}
                  value={eventLocation}
                  onChange={(e) => setEventLocation(e.target.value)}
                  variant="outlined"
                  size="small"
                />
              </Box>

              {/* Spalte 2: Start-/End- Date/Time */}
              <Box sx={{ flex: 1.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Zeile 1: Startdatum und Startzeit */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <CustomDatePicker
                    label={t('chat.event.startDate', 'Startdatum')}
                    value={eventStartDate}
                    onChange={(e) => handleEventStartDateChange(e.target.value)}
                    required
                    sx={{ flex: 1 }}
                  />
                  <CustomTimePicker
                    label={t('chat.event.startTime', 'Startzeit')}
                    value={eventStartTime}
                    onChange={(e) => handleEventStartTimeChange(e.target.value)}
                    required
                    sx={{ flex: 1 }}
                  />
                </Box>
                
                {/* Zeile 2: Enddatum und Endzeit */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <CustomDatePicker
                    label={t('chat.event.endDate', 'Enddatum')}
                    value={eventEndDate}
                    onChange={(e) => setEventEndDate(e.target.value)}
                    required
                    inputProps={{ min: eventStartDate }}
                    sx={{ flex: 1 }}
                  />
                  <CustomTimePicker
                    label={t('chat.event.endTime', 'Endzeit')}
                    value={eventEndTime}
                    onChange={(e) => setEventEndTime(e.target.value)}
                    sx={{ flex: 1 }}
                  />
                </Box>
              </Box>

              {/* Spalte 3: Description */}
              <Box sx={{ flex: 1.5, display: 'flex', flexDirection: 'column' }}>
                <TextField
                  fullWidth
                  multiline
                  label={t('chat.event.description', 'Beschreibung')}
                  placeholder={t('chat.event.descriptionPlaceholder', 'Ereignis-Beschreibung')}
                  value={eventDescription}
                  onChange={(e) => setEventDescription(e.target.value)}
                  variant="outlined"
                  size="small"
                  sx={{ height: '100%' }}
                  InputProps={{
                    sx: { height: '100%', alignItems: 'flex-start' }
                  }}
                />
              </Box>

              {/* Spalte 4: Send Button */}
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                justifyContent: 'flex-end',
                minWidth: '100px'
              }}>
                <Button 
                  onClick={handleSendEvent}
                  variant="contained"
                  className="button"
                  disabled={eventTitle.trim() === '' || eventStartDate === '' || eventStartTime === ''}
                  startIcon={<SendIcon />}
                  size="small"
                  fullWidth
                  sx={{ py: 1 }}
                >
                  {t('chat.event.send', 'Senden')}
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        {orientation === 'portrait' && (
          <DialogActions sx={{ p: 2, pt: 0 }}>
            <Button 
              onClick={handleSendEvent}
              variant="contained"
              className="button"
              disabled={eventTitle.trim() === '' || eventStartDate === '' || eventStartTime === ''}
              startIcon={<SendIcon />}
              size="small"
            >
              {t('chat.event.send', 'Senden')}
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* Survey Dialog */}
      <Dialog
        open={surveyDialogOpen}
        onClose={handleSurveyDialogClose}
        maxWidth={orientation === 'landscape' ? 'md' : 'sm'}
        fullWidth
        PaperProps={{
          sx: {
            height: orientation === 'landscape' ? '400px' : 'auto'
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{t('chat.survey.createTitle', 'Umfrage erstellen')}</Typography>
            <IconButton onClick={handleSurveyDialogClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {orientation === 'portrait' ? (
            // Portrait: Alle Felder untereinander
            <>
              <Box sx={{ mb: 2, mt: 1 }}>
                <TextField
                  autoFocus
                  fullWidth
                  label={t('chat.survey.question', 'Frage')}
                  value={surveyQuestion}
                  onChange={(e) => setSurveyQuestion(e.target.value)}
                  placeholder={t('chat.survey.questionPlaceholder', 'Stelle deine Frage')}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.primary" gutterBottom sx={{ fontWeight: 600 }}>
                  {t('chat.survey.options', 'Optionen')}
                </Typography>
                {surveyOptions.map((option, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField
                      fullWidth
                      placeholder={t('chat.survey.optionPlaceholder', 'Option hinzufügen')}
                      value={option}
                      onChange={(e) => handleSurveyOptionChange(index, e.target.value)}
                      onKeyPress={(e) => handleSurveyOptionKeyPress(e, index)}
                      size="small"
                      inputProps={{ 'data-option-index': index }}
                    />
                    {surveyOptions.length > 1 && (
                      <IconButton 
                        size="small" 
                        onClick={() => handleRemoveSurveyOption(index)}
                        sx={{ color: 'text.secondary' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                ))}
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    checked={surveyMultipleChoice}
                    onChange={(e) => setSurveyMultipleChoice(e.target.checked)}
                    color="primary"
                  />
                }
                label={t('chat.survey.multipleChoice', 'Mehrere Antworten erlauben')}
              />
            </>
          ) : (
            // Landscape: 4-Spalten-Layout
            <Box sx={{ display: 'flex', gap: 2, height: '100%', mt: 1 }}>
              {/* Spalte 1: Create Poll + Multiple Choice */}
              <Box sx={{ minWidth: '120px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold' }}>
                  {t('chat.survey.createPoll', 'Create Poll')}
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={surveyMultipleChoice}
                      onChange={(e) => setSurveyMultipleChoice(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={t('chat.survey.multipleChoice', 'Mehrere Antworten erlauben')}
                  sx={{ 
                    '& .MuiFormControlLabel-label': { 
                      fontSize: '0.85rem' 
                    } 
                  }}
                />
              </Box>

              {/* Spalte 2: Question */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="subtitle2" color="text.primary" gutterBottom sx={{ fontWeight: 600 }}>
                  {t('chat.survey.question', 'Frage')}
                </Typography>
                <TextField
                  autoFocus
                  fullWidth
                  placeholder={t('chat.survey.questionPlaceholder', 'Stelle deine Frage')}
                  value={surveyQuestion}
                  onChange={(e) => setSurveyQuestion(e.target.value)}
                  multiline
                  rows={8}
                  sx={{ flex: 1 }}
                />
              </Box>

              {/* Spalte 3: Answers */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="subtitle2" color="text.primary" gutterBottom sx={{ fontWeight: 600 }}>
                  {t('chat.survey.answers', 'Antworten')}
                </Typography>
                <Box sx={{ flex: 1, overflow: 'auto' }}>
                  {surveyOptions.map((option, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <TextField
                        fullWidth
                        placeholder={t('chat.survey.optionPlaceholder', 'Option hinzufügen')}
                        value={option}
                        onChange={(e) => handleSurveyOptionChange(index, e.target.value)}
                        onKeyPress={(e) => handleSurveyOptionKeyPress(e, index)}
                        size="small"
                        inputProps={{ 'data-option-index': index }}
                      />
                      {surveyOptions.length > 1 && (
                        <IconButton 
                          size="small" 
                          onClick={() => handleRemoveSurveyOption(index)}
                          sx={{ color: 'text.secondary' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Spalte 4: Send Button */}
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                justifyContent: 'flex-end',
                minWidth: '100px'
              }}>
                <Button 
                  onClick={handleSendSurvey}
                  variant="contained"
                  className="button"
                  disabled={surveyQuestion.trim() === '' || surveyOptions.filter(o => o.trim() !== '').length < 2}
                  startIcon={<SendIcon />}
                  size="small"
                  fullWidth
                  sx={{ py: 1 }}
                >
                  {t('chat.survey.send', 'Senden')}
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        {orientation === 'portrait' && (
          <DialogActions sx={{ p: 2, pt: 0 }}>
            <Button 
              onClick={handleSendSurvey}
              variant="contained"
              className="button"
              disabled={surveyQuestion.trim() === '' || surveyOptions.filter(o => o.trim() !== '').length < 2}
              startIcon={<SendIcon />}
              size="small"
            >
              {t('chat.survey.send', 'Senden')}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </Box>
  );
};

export default MTRChatMini;